import { useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  InputBase,
  Paper,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { colors } from '../../../utils/colors';
import CreateFolderDialog from './CreateFolderDialog';

const FolderList = ({ folders, onFolderClick, error, customerId, projectId, onFolderCreated }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleFolderCreated = () => {
    handleCloseDialog();
    if (onFolderCreated) {
      onFolderCreated();
    }
  };

  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2.5, sm: 3 },
          gap: { xs: 1.5, sm: 2 }
        }}
      >
        <Paper
          component="form"
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: '300px', md: '350px' },
            bgcolor: colors.background.secondary,
            border: `1px solid ${colors.border.main}`,
            borderRadius: 2,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.75, sm: 0.875 },
            boxShadow: 'none',
            '&:hover': {
              borderColor: colors.primary.main
            },
            '&:focus-within': {
              borderColor: colors.primary.main,
              boxShadow: `0 0 0 2px ${colors.primary.main}20`
            }
          }}
          onSubmit={(e) => e.preventDefault()}
        >
          <SearchIcon sx={{ color: colors.text.secondary, mr: 1, fontSize: { xs: 20, sm: 24 } }} />
          <InputBase
            placeholder="Search folders..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              flex: 1,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              color: colors.text.primary
            }}
          />
        </Paper>

        {/* Create New Folder Button */}
        {/* <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
          onClick={handleOpenDialog}
          sx={{
            bgcolor: colors.primary.main,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 0.875, sm: 1.125 },
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            boxShadow: `0 4px 12px ${colors.primary.main}40`,
            '&:hover': {
              bgcolor: colors.primary.dark,
              boxShadow: `0 6px 16px ${colors.primary.main}50`
            }
          }}
        >
          Create New Folder
        </Button> */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 2.5 } }}>
          {error}
        </Alert>
      )}

      {filteredFolders.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 6, sm: 8 }
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: colors.text.tertiary,
              mb: 1,
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
            }}
          >
            No Folders Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              fontSize: { xs: '0.813rem', sm: '0.875rem' }
            }}
          >
            {searchQuery ? 'No folders match your search.' : 'Create a new folder to get started.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {filteredFolders.map((folder) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
              <Card
                elevation={0}
                onClick={() => onFolderClick(folder)}
                sx={{
                  height: '100%',
                  borderRadius: { xs: 2, sm: 2.5, md: 3 },
                  border: `1px solid ${colors.alpha.black08}`,
                  bgcolor: colors.background.primary,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: colors.primary.main,
                    boxShadow: `0 4px 12px ${colors.primary.main}20`
                  }
                }}
              >
                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: 1 }}>
                    <FolderIcon
                      sx={{
                        color: colors.primary.main,
                        fontSize: { xs: 36, sm: 40 }
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: colors.text.primary,
                          fontSize: { xs: '0.938rem', sm: '1rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {folder.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.text.secondary,
                          fontSize: { xs: '0.688rem', sm: '0.75rem' }
                        }}
                      >
                        {new Date(folder.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      <CreateFolderDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onFolderCreated={handleFolderCreated}
        customerId={customerId}
        projectId={projectId}
      />
    </Box>
  );
};

export default FolderList;
